/**
 * Local dispatch: the service worker handing a /groups/ request to a tab on
 * this same device (stream-request-bodies-across-the-sw-postmessage-edge).
 *
 * Both halves live here so they can be tested together:
 *
 *   SW side    takeRequestBody()      — Blob by reference, or the live
 *                                        request stream where the tab can
 *                                        take one and the platform can
 *                                        transfer it
 *              createLocalDispatcher() — pending requests with an idle
 *                                        timeout re-armed by progress:
 *                                        body bytes flowing (SW-counted),
 *                                        then proxiedProgress pings
 *   page side  prepareProxiedBody()   — what the route handler receives
 *              startProgressPings()   — keep the SW's timer alive while a
 *                                        handler works past the last byte
 *
 * A request body is never materialized as an ArrayBuffer in the SW: a Blob
 * posts by reference (no structured-clone copy), and a transferred stream
 * flows at chunk-sized memory. Buffering happens only in the tab, and only
 * for routes that did not register `metadata.streaming`.
 */

/** Idle window. Matches the old fixed ceiling, so a page that never pings
 *  (an older acequia) gets at least the time it always had. */
export const LOCAL_IDLE_MS = 120000

/** Page-side ping interval — well inside the idle window. */
export const PROGRESS_PING_MS = 15000

/** Methods whose body is not read. */
const NO_BODY = new Set(['GET', 'HEAD'])

/**
 * The size hint a page may put on an upload. A service worker cannot see a
 * request body's size: no engine puts Content-Length on the intercepted
 * request, and in Safari touching the body at all — one chunk of the stream,
 * then cancel — buffers the whole of it in the worker process (measured
 * 2026-11-04: +800 MB for a 512 MB body; untouched, +0). So the only size the
 * worker can act on without paying for it is one the page declares. acequia's
 * own uploaders (acequia.sync, the file browser) set it from `blob.size`; any
 * caller can.
 */
export const BODY_SIZE_HEADER = 'X-Acequia-Body-Size'

/**
 * Above this declared size a WebKit upload to a local route goes over the
 * network relay instead of local dispatch (service-worker-behaviour-across-
 * browsers: local dispatch costs Safari about 1.7× the body in memory and a
 * Blob under memory pressure arrives dead; the relay stays flat). 256 MiB:
 * 256 MB never lost a Blob in the bench, 1 GB lost one in eight.
 * `config.acequia.localBodyMax` overrides; `Infinity` keeps everything local.
 */
export const DEFAULT_LOCAL_BODY_MAX = 256 * 1024 * 1024

/**
 * The body size a request declares, or null.
 * @param {Request} request
 * @returns {number|null}
 */
export function declaredBodySize(request) {
    const raw = request?.headers?.get?.(BODY_SIZE_HEADER)
    if (raw == null || raw === '') return null
    const n = Number(raw)
    return Number.isFinite(n) && n >= 0 ? n : null
}

let transferableStreams = null

/**
 * Whether this realm can transfer a ReadableStream (postMessage transfer
 * list). Probed once with structuredClone, which uses the same serializer.
 */
export function canTransferStreams() {
    if (transferableStreams !== null) return transferableStreams
    try {
        const rs = new ReadableStream()
        structuredClone(rs, { transfer: [rs] })
        transferableStreams = true
    } catch {
        transferableStreams = false
    }
    return transferableStreams
}

/**
 * Whether a posted request Blob can still be read in this tab. Under memory
 * pressure Safari delivers a SW-posted Blob already dead: every read throws
 * `NotFoundError` (service-worker-behaviour-across-browsers). A handler given
 * one fails late with nothing written, so the tab checks first. Reads one
 * byte.
 *
 * @param {object} request - the proxiedRequest message
 * @returns {Promise<boolean>}
 */
export async function postedBodyReadable(request) {
    const body = request.body
    if (typeof Blob === 'undefined' || !(body instanceof Blob) || body.size === 0) return true
    try {
        await body.slice(body.size - 1).arrayBuffer()
        return true
    } catch {
        return false
    }
}

/** The proxiedResponse data for a Blob that arrived unreadable. */
export function bodyLostResponse() {
    return {
        status: 503,
        statusText: 'Service Unavailable',
        body:
            'The browser lost this upload between its service worker and the serving tab. ' +
            'Safari does this when it is short of memory. Nothing was written; try again, ' +
            'or upload a smaller file.',
        headers: { 'content-type': 'text/plain; charset=utf-8', 'x-acequia-error': 'body-lost' },
    }
}

/** Test seam: forget the probe result. */
export function _resetTransferProbe(value = null) {
    transferableStreams = value
}

/**
 * Whether this engine keeps a spare clone of a request for the network
 * fall-through (see takeRequestBody). WebKit only: Safari can't read a File
 * body of 2 GiB+ in a SW, a failed read leaves the request unsendable, and
 * the spare costs it nothing. In Chrome the spare would cost the by-reference
 * Blob (the body gets copied into disk-limited blob storage), and Chrome
 * reads these bodies fine without it (service-worker-behaviour-across-browsers).
 * Every iOS browser is WebKit, so only desktop Chromium names are excluded.
 *
 * @param {string} [userAgent]
 * @returns {boolean}
 */
export function keepsSpare(userAgent = typeof navigator !== 'undefined' ? navigator.userAgent : '') {
    return /AppleWebKit\//.test(userAgent) && !/(Chrome|Chromium|Edg|OPR)\//.test(userAgent)
}

/**
 * The body to post for a request.
 *
 * @param {Request} request
 * @param {object} [opts]
 * @param {boolean} [opts.acceptsStream] - the target tab advertised
 *   `caps.bodyStream` when it registered
 * @param {boolean} [opts.spare] - keep an unread clone for the network
 *   fall-through (default: keepsSpare())
 * @param {number} [opts.localBodyMax] - on a spare-keeping engine (WebKit), a
 *   body whose declared size (BODY_SIZE_HEADER) is this or more is not read
 *   at all: `relay` is the untouched request, for the network relay
 *   (default: DEFAULT_LOCAL_BODY_MAX; Infinity keeps everything local)
 * @returns {Promise<{ body?: Blob|null, bodyStream?: ReadableStream, unreadable?: Error, spare?: Request, relay?: Request, size?: number }>}
 *   `unreadable` when the SW could not read the body, with `spare` to send
 *   to the network instead when one was kept; `relay` when the body was
 *   never read because the page declared it too large for local dispatch here
 */
export async function takeRequestBody(request, { acceptsStream = false, spare: keep = keepsSpare(), localBodyMax = DEFAULT_LOCAL_BODY_MAX } = {}) {
    if (NO_BODY.has(request.method)) return { body: null }
    if (keep && localBodyMax != null) {
        const size = declaredBodySize(request)
        if (size != null && size >= localBodyMax) return { relay: request, size }
    }
    if (acceptsStream && request.body instanceof ReadableStream && canTransferStreams()) {
        return { bodyStream: request.body }
    }
    // Read the original, never a clone: in Firefox a Blob read from a clone
    // hangs in the tab while the original stays unread.
    const spare = keep ? request.clone() : null
    try {
        const body = await request.blob()
        spare?.body?.cancel().catch(() => {})
        return { body }
    } catch (unreadable) {
        return spare ? { unreadable, spare } : { unreadable }
    }
}

/**
 * The answer when the SW cannot read a request's body and kept no spare to
 * send instead: a 500 that names the error, not the catch-all's bare
 * "Internal Server Error".
 *
 * @param {Error} error - what reading the body threw
 * @returns {Response}
 */
export function unreadableBodyResponse(error) {
    return new Response(`The service worker could not read the request body: ${error?.message || error}`, {
        status: 500,
        headers: { 'Content-Type': 'text/plain; charset=utf-8', 'X-Acequia-Error': 'sw-body-unreadable' },
    })
}

/**
 * Pending local requests, each with an idle timer that progress re-arms.
 *
 * @param {object} [opts]
 * @param {number} [opts.idleMs]
 * @returns {{
 *   send(client: { postMessage: Function }, request: object, opts?: { bodyStream?: ReadableStream }): Promise<object>,
 *   resolve(requestId: string, response: object): boolean,
 *   progress(requestId: string): boolean,
 *   ids(): string[],
 * }}
 */
export function createLocalDispatcher({ idleMs = LOCAL_IDLE_MS } = {}) {
    const pending = new Map()

    const arm = (requestId) => {
        const entry = pending.get(requestId)
        if (!entry) return false
        clearTimeout(entry.timer)
        entry.timer = setTimeout(() => {
            pending.delete(requestId)
            entry.reject(new Error('Request timeout'))
        }, idleMs)
        return true
    }

    return {
        send(client, request, { bodyStream } = {}) {
            return new Promise((resolve, reject) => {
                const { requestId } = request
                pending.set(requestId, { resolve, reject, timer: null })
                arm(requestId)

                if (!bodyStream) {
                    client.postMessage(request)
                    return
                }

                // Count the body through: every chunk the tab pulls re-arms
                // the timer, so a multi-GB upload is limited by stalls, not
                // by its total duration.
                const counted = bodyStream.pipeThrough(
                    new TransformStream({
                        transform(chunk, controller) {
                            arm(requestId)
                            controller.enqueue(chunk)
                        },
                    })
                )
                try {
                    client.postMessage({ ...request, body: null, bodyStream: counted }, [counted])
                } catch (e) {
                    // The transfer was refused after all (the probe can't see
                    // every platform limit): fall back to the Blob path. The
                    // pipe is still running, so the body is intact.
                    new Response(counted)
                        .blob()
                        .then((body) => client.postMessage({ ...request, body }))
                        .catch((err) => {
                            const entry = pending.get(requestId)
                            if (!entry) return
                            clearTimeout(entry.timer)
                            pending.delete(requestId)
                            reject(err)
                        })
                }
            })
        },

        resolve(requestId, response) {
            const entry = pending.get(requestId)
            if (!entry) return false
            clearTimeout(entry.timer)
            pending.delete(requestId)
            entry.resolve(response)
            return true
        },

        progress(requestId) {
            return arm(requestId)
        },

        ids() {
            return [...pending.keys()]
        },
    }
}

/**
 * What a route handler receives, from a proxiedRequest as the SW posted it.
 *
 * Streaming routes get `bodyStream` (a Blob body is streamed with
 * `blob.stream()`, so they read at chunk-sized memory either way). Other
 * routes get the buffered, normalized body they always have.
 *
 * @param {object} request - the proxiedRequest message
 * @param {object} opts
 * @param {boolean} opts.streaming - the matched route's metadata.streaming
 * @param {Function} opts.normalizeBody - requestNormalize.normalizeBody
 * @returns {Promise<{ body: any, rawBody: ArrayBuffer|null, bodyStream?: ReadableStream }>}
 */
export async function prepareProxiedBody(request, { streaming, normalizeBody }) {
    const hasBlob = typeof Blob !== 'undefined' && request.body instanceof Blob
    if (streaming) {
        if (request.bodyStream) return { body: null, rawBody: null, bodyStream: request.bodyStream }
        if (hasBlob) return { body: null, rawBody: null, bodyStream: request.body.stream() }
    }
    let raw = request.body
    if (request.bodyStream) raw = await new Response(request.bodyStream).arrayBuffer()
    else if (hasBlob) raw = await raw.arrayBuffer()
    return normalizeBody(raw, request.headers?.['content-type'])
}

/**
 * Post a proxiedProgress ping for `requestId` every `intervalMs` until the
 * returned stop function is called.
 *
 * @param {(msg: object) => void} post
 * @param {string} requestId
 * @param {number} [intervalMs]
 * @returns {() => void} stop
 */
export function startProgressPings(post, requestId, intervalMs = PROGRESS_PING_MS) {
    const timer = setInterval(() => {
        try {
            post({ type: 'proxiedProgress', requestId })
        } catch {
            // The SW went away — the response post will fail the same way.
        }
    }, intervalMs)
    return () => clearInterval(timer)
}
